import React, { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { aiInterviewsAPI, errMsg } from '../../../lib/aiInterviewApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { ADVISORY, DECISION_LABEL, fmtDate, REC_TONE } from '../recruiter/common';

export function AdvisoryBanner({ report }) {
  const rec = report.recommendation;
  return (
    <>
      <div className="iv-advisory" data-testid="report-advisory-banner">
        <div>
          <small>AI-GENERATED, ADVISORY ONLY</small>
          <div className="iv-rec" data-testid="report-advisory-label" style={{ color: REC_TONE[rec] === 'green' ? '#6EE7B7' : REC_TONE[rec] === 'amber' ? '#FCD34D' : '#FCA5A5' }}>{ADVISORY[rec] || rec}</div>
          <div style={{ fontSize: '0.85rem', opacity: 0.85 }} data-testid="report-score-summary">{rec} · weighted {report.weighted_score}/5 · {report.score_100}/100</div>
        </div>
        <div style={{ maxWidth: 560, fontSize: '0.88rem', lineHeight: 1.55 }}>
          {report.recommendation_rationale}
          <small style={{ marginTop: '0.4rem' }}>People on your team make every decision. AI scores and integrity flags never reject a candidate on their own.</small>
        </div>
      </div>
      {report.must_have_below_bar && (
        <div className="iv-note is-warn" style={{ marginBottom: '1rem' }} data-testid="report-must-have-warning">
          <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />At least one must-have competency scored below the bar, so the recommendation is capped.
        </div>
      )}
    </>
  );
}

const ACTIONS = [
  ['advance', 'Advance stage', 'iv-btn-primary', 'report-advance-btn'],
  ['bgv', 'Proceed to BGV', 'iv-btn-ghost', 'report-bgv-btn'],
  ['hold', 'Hold', 'iv-btn-ghost', 'report-hold-btn'],
  ['reject', 'Reject', 'iv-btn-danger', 'report-reject-btn'],
];

export function DecisionBar({ d, onDone }) {
  const [act, setAct] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await aiInterviewsAPI.decision(d.id, { action: act, note });
      toast.success(data.stage ? `Decision recorded. Candidate moved to ${data.stage}.` : 'Decision recorded');
      setAct(null);
      setNote('');
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not record the decision'));
    } finally {
      setBusy(false);
    }
  };
  const label = ACTIONS.find((a) => a[0] === act)?.[1];
  return (
    <div className="iv-card iv-row" style={{ justifyContent: 'space-between' }} data-testid="report-decision-bar">
      <div>
        <div className="iv-eyebrow">Human decision</div>
        <div className="iv-small" data-testid="report-current-decision">
          {d.decision ? `${DECISION_LABEL[d.decision.action]} by ${d.decision.by} · ${fmtDate(d.decision.at)}${d.decision.note ? ` — “${d.decision.note}”` : ''}` : 'No decision yet.'}
        </div>
        {d.integrity?.review_status === 'pending' && <div className="iv-small" style={{ color: '#B45309', marginTop: 4 }}>Integrity review is pending. Consider reviewing it first.</div>}
      </div>
      <div className="iv-row">
        {ACTIONS.map(([k, l, cls, tid]) => <button type="button" key={k} className={`iv-btn ${cls}`} onClick={() => setAct(k)} data-testid={tid}>{l}</button>)}
      </div>
      <Dialog open={!!act} onOpenChange={(o) => !o && setAct(null)}>
        <DialogContent className="iv" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>{act === 'reject' ? 'A reason is required. It is stored in the audit log and pipeline history.' : 'Optionally add a note for your team.'}</DialogDescription>
          </DialogHeader>
          <textarea className="iv-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={act === 'reject' ? 'Reason for rejection' : 'Note (optional)'} data-testid="decision-note-input" />
          <div className="iv-row-end">
            <button type="button" className="iv-btn iv-btn-ghost" onClick={() => setAct(null)}>Cancel</button>
            <button type="button" className="iv-btn iv-btn-primary" disabled={busy || (act === 'reject' && !note.trim())} onClick={submit} data-testid="decision-confirm-btn">{busy ? 'Saving…' : `Confirm: ${label}`}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
